import { State } from 'redux/initialState';
import { Partner } from 'types';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { societyHasTitles } from 'utils/societyHasTitles';
import { useGetSocietyShareTitleDocument } from 'modules/partners/pages/ShareTitleList/hooks/useGetSocietyShareTitleDocument';
import { PartnerOperationsContextType } from '../types/PartnerOperationsContextType';
import { partnerOperationsInitValue } from '../constants/partnerOperationsInitValue';
import { useGetPartnerOperations } from '../hooks/useGetPartnerOperations';

const PartnerOperationsContext = createContext<PartnerOperationsContextType>(
  partnerOperationsInitValue
);

export const PartnerOperationsProvider: FC<{
  children: ReactNode;
  partner: Partner;
}> = ({ children, partner }) => {
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const hasTitlesEnabled = societyHasTitles(actualSociety);

  const { data: partnerOperationsData, isLoading } = useGetPartnerOperations({
    partnerId: partner._id,
    page,
    rowsPerPage,
  });

  const { data: societySharesTitleData } = useGetSocietyShareTitleDocument({
    society: actualSociety,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleExpandRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <PartnerOperationsContext.Provider
      value={{
        data: {
          societyId: actualSociety?._id || '',
          societyName: actualSociety?.name || '',
          partner,
          totalOperations: partnerOperationsData.totalOperations,
          operations: partnerOperationsData.operations,
          sharesTitles: partnerOperationsData.sharesTitles,
          titleDocument: societySharesTitleData.document,
          page,
          rowsPerPage,
          expandedRows,
          hasTitlesEnabled,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleExpandRow,
        },
      }}
    >
      {children}
    </PartnerOperationsContext.Provider>
  );
};

export const usePartnerOperations = () => {
  const context = useContext(PartnerOperationsContext);
  if (!context) {
    throw new Error(
      'usePartnerOperations must be used within a PartnerOperationsProvider'
    );
  }
  return context;
};
